/**
 * This HOC is similiar to react-redux's `connect` HOC in that it's curried.
 * It has the following signature:
 *   (?(
 *      | PartialLearningTimeInfo
 *      | (Config => PartialLearningTimeInfo)
 *     )
 *   ) => (WrappedComponent) => Component
 *
 * This allows us to use it with `compose` which is helpful since in many
 * places we use `withConversionContext` with other HOCs such as `withRouter`,
 * `withUserProfile`, etc.
 *
 * The optional first arg when supplied can be either a `PartialLearningTimeInfo`
 * or a function which takes `WrappedComponent`'s `Config` and returns a
 * `PartialLearningTimeInfo`.
 *
 * `Config` is the type a component's props taking into account defaultProps if
 * any exist.
 *
 * `WrappedComponent` is the component into which `withConversionContext` is
 * injecting a `markConversion` prop.  `Component` is the resulting component
 * which has no `markConversion` ans is exported for use by consumers.
 *
 * Examples:
 * - export default withConversionContext()(Foo);
 * - export default withConversionContext({activity: enums.Activity.NAVIGATING})(Foo);
 * - export default withConversionContext(props => {activity: props.activity})(Foo);
 * - export default compose(
 *     withConversionContext({activity: enums.Activity.NAVIGATING}),
 *     withRouter,
 *   )(Foo);
 */
import * as React from "react";

import {ConversionContextProvider} from "./conversion-context-provider.tsx";
import type {PartialLearningTimeInfo} from "./types.ts";

export const withConversionContext =
    <Config extends object, C extends React.ComponentType<Config>>(
        learningTimeInfo:
            | PartialLearningTimeInfo
            | ((arg1: Config) => PartialLearningTimeInfo),
    ) =>
    (WrappedComponent: C): React.ComponentType<Config> => {
        return (props: Config) => (
            <ConversionContextProvider
                learningTimeInfo={
                    typeof learningTimeInfo === "function"
                        ? learningTimeInfo(props)
                        : learningTimeInfo
                }
            >
                <WrappedComponent {...(props as any)} />
            </ConversionContextProvider>
        );
    };
